import type { OpenInterval } from "@suse-o11y/date-time/open-interval";
import { max } from "@suse-o11y/max";
import { min } from "@suse-o11y/min";
import { type GetCssVar, useCssVars } from "@suse-o11y/use-css-vars";
import React from "react";

import { Group, type GroupProps, Rect, useContainerSize } from "../_canvas";

import { useTimelineRange, useUpdateFrozenTimeRangeOnChange } from "./_context";
import { getMutedSegments } from "./mute";

export interface TimelineMutedStateProps extends Pick<GroupProps, "x" | "y"> {
	muteIntervals: ReadonlyArray<OpenInterval>;
}

export const TimelineMutedState: React.FC<TimelineMutedStateProps> = ({ muteIntervals, x, y }) => {
	const [setElement, getCssVar] = useCssVars<HTMLDivElement>();
	const { width } = useContainerSize();
	const [timelineStart, timelineEnd] = useTimelineRange();

	const mutedSegments = React.useMemo(
		() =>
			getMutedSegments({
				intervals: muteIntervals,
				timelineWidth: width,
				timelineStart,
				timelineEnd,
				minWidth: 4,
				gap: 2,
			}),
		[muteIntervals, width, timelineStart, timelineEnd],
	);

	useUpdateFrozenTimeRangeOnChange([muteIntervals]);

	return (
		<div className="contents" ref={setElement}>
			<Group x={x} y={y}>
				{mutedSegments.map(({ leftPx, rightPx }) => {
					if (leftPx >= rightPx) {
						return null;
					}

					return (
						<MuteLine
							end={min(width, rightPx)}
							getCssVar={getCssVar}
							isClippedLeft={leftPx <= 0}
							isClippedRight={rightPx >= width}
							key={leftPx}
							start={max(0, leftPx)}
						/>
					);
				})}
			</Group>
		</div>
	);
};

const MuteLine: React.FC<{
	start: number;
	end: number;
	getCssVar: GetCssVar;
	isClippedLeft: boolean;
	isClippedRight: boolean;
}> = ({ start, end, getCssVar, isClippedLeft, isClippedRight }) => {
	const height = 9;
	const width = end - start;
	const cornerRadius = height;

	if (width <= 0) {
		return null;
	}

	return (
		<Rect
			cornerRadius={[
				isClippedLeft ? 0 : cornerRadius,
				isClippedRight ? 0 : cornerRadius,
				isClippedRight ? 0 : cornerRadius,
				isClippedLeft ? 0 : cornerRadius,
			]}
			fill={getCssVar("--datavix-fill-health-timeline-muted")}
			height={height}
			stroke={getCssVar("--dataviz-stroke-health-timeline-muted")}
			strokeWidth={1}
			width={width}
			x={start}
			y={-height / 2}
		/>
	);
};
